import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  People as PeopleIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';

interface MasqueradeHeaderProps {
  totalUsers?: number;
  siteAdminRestricted?: boolean;
  currentFilters?: {
    search?: string;
    role?: string;
    firm?: string;
  };
}

const MasqueradeHeader: React.FC<MasqueradeHeaderProps> = ({
  totalUsers = 0,
  siteAdminRestricted = false,
  currentFilters = {}
}) => {
  const hasActiveFilters = Boolean(
    currentFilters.search || 
    currentFilters.role || 
    currentFilters.firm
  );

  return (
    <Box sx={{ p: 4, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: { xs: 'flex-start', md: 'center' } }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            User Masquerade
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {siteAdminRestricted
              ? 'Select a Site Admin or lower-level user to masquerade as. PMP Admin users are not available.'
              : 'Select any active user to masquerade as. This will switch your session to act as that user.'}
          </Typography>
          
          {hasActiveFilters && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <FilterIcon color="action" sx={{ fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Active filters:
              </Typography>
              <FilterSummary filters={currentFilters} />
            </Box>
          )}
        </Box>
        
        <Box sx={{ minWidth: { xs: '100%', md: 200 } }}>
          <Card elevation={1} sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <PeopleIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h3" component="div" sx={{ fontWeight: 600 }}>
                {totalUsers}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total Users
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

interface FilterSummaryProps {
  filters: {
    search?: string;
    role?: string;
    firm?: string;
  };
}

const FilterSummary: React.FC<FilterSummaryProps> = ({ filters }) => {
  const activeFilters = [];
  
  if (filters.search) {
    activeFilters.push(
      <Chip 
        key="search" 
        label={`Search: "${filters.search}"`} 
        size="small" 
        color="primary" 
        variant="outlined"
      />
    );
  }
  
  if (filters.role) {
    activeFilters.push(
      <Chip 
        key="role" 
        label={`Role: ${filters.role.replace('_', ' ')}`} 
        size="small" 
        color="secondary" 
        variant="outlined"
      />
    );
  }
  
  if (filters.firm) {
    activeFilters.push(
      <Chip 
        key="firm" 
        label={`Firm: ${filters.firm}`} 
        size="small" 
        color="info" 
        variant="outlined"
      />
    );
  }
  
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {activeFilters}
    </Box>
  );
};

export default MasqueradeHeader;
